import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const MAX = 24;

export const recordProductView = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((input: unknown) =>
    z.object({ productId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const sb = (context as any).supabase;
    const userId = (context as any).userId as string;
    const { error } = await sb
      .from("product_view_history")
      .upsert(
        { user_id: userId, product_id: data.productId, viewed_at: new Date().toISOString() },
        { onConflict: "user_id,product_id" },
      );
    if (error) {
      // never break the PDP because of history
      console.error("recordProductView", error);
      return { ok: false };
    }
    return { ok: true };
  });

export const getViewHistoryIds = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const sb = (context as any).supabase;
    const userId = (context as any).userId as string;
    const { data, error } = await sb
      .from("product_view_history")
      .select("product_id, viewed_at")
      .eq("user_id", userId)
      .order("viewed_at", { ascending: false })
      .limit(MAX);
    if (error) {
      console.error("getViewHistoryIds", error);
      return [] as string[];
    }
    return (data ?? []).map((r: { product_id: string }) => r.product_id);
  });

export const mergeViewHistory = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((input: unknown) =>
    z
      .object({
        localIds: z.array(z.string().uuid()).max(MAX).default([]),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const sb = (context as any).supabase;
    const userId = (context as any).userId as string;
    if (!data.localIds.length) return { merged: 0 };

    // Load which of those already exist so we don't overwrite fresher viewed_at
    const { data: existing } = await sb
      .from("product_view_history")
      .select("product_id")
      .eq("user_id", userId)
      .in("product_id", data.localIds);
    const have = new Set((existing ?? []).map((r: { product_id: string }) => r.product_id));

    const now = Date.now();
    const rows = data.localIds
      .filter((id) => !have.has(id))
      // Preserve local order: earliest in the array = most recent viewed
      .map((id, i) => ({
        user_id: userId,
        product_id: id,
        viewed_at: new Date(now - (i + 1) * 60_000).toISOString(),
      }));

    if (!rows.length) return { merged: 0 };
    const { error } = await sb.from("product_view_history").insert(rows);
    if (error) {
      console.error("mergeViewHistory", error);
      return { merged: 0 };
    }
    return { merged: rows.length };
  });

export const clearViewHistory = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const sb = (context as any).supabase;
    const userId = (context as any).userId as string;
    const { error } = await sb.from("product_view_history").delete().eq("user_id", userId);
    if (error) {
      console.error("clearViewHistory", error);
      return { ok: false };
    }
    return { ok: true };
  });
